// Consulta compacta para el checkout oficial vigente: muestra el precio real
// que cobra la ventana oficial y un único botón. La selección definitiva de
// fecha, horario y viajeros ocurre una sola vez, dentro de esa ventana.
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { ShieldCheck, Tag } from "lucide-react";
import { getOfficialChannelPrice } from "@/lib/official-channel-price.functions";
import { useCurrency, useMoney } from "@/lib/currency";

export interface OfficialCategory {
  id: string;
  title: string;
  minAge: number | null;
  maxAge: number | null;
  ticketCategory?: string | null;
  groupSize?: number | null;
}

export function useOfficialPrice(activityId: string | null, cats: OfficialCategory[]) {
  const fetchPrice = useServerFn(getOfficialChannelPrice);
  // Misma moneda que el calendario oficial: Bókun tiene tarifas propias por moneda.
  const { currency } = useCurrency();
  const categoryIds = cats.map((c) => String(c.id)).filter((id) => /^\d+$/.test(id));
  const groupIds = cats
    .filter((c) => (c.ticketCategory ?? "").toUpperCase() === "GROUP")
    .map((c) => String(c.id))
    .filter((id) => /^\d+$/.test(id));
  return useQuery({
    queryKey: ["official-channel-price", activityId, categoryIds.join(","), currency],
    enabled: !!activityId && categoryIds.length > 0,
    staleTime: 10 * 60_000,
    queryFn: () => {
      if (!activityId) throw new Error("Missing activity");
      return fetchPrice({ data: { activityId, categoryIds, groupIds, currency } });
    },
  });
}

/** Menor importe positivo del canal oficial (para "Desde"). */
export function officialFrom(
  prices: Record<string, number> | undefined,
  categories?: { id: string | number; title?: string | null }[],
): number | null {
  // Precio de adulto (no el mínimo, que suele ser de niño).
  const adult = (categories ?? []).find((c) => /adult|adulto/i.test(c.title ?? "") && !/mayor|senior/i.test(c.title ?? ""));
  if (adult && prices?.[String(adult.id)] && prices[String(adult.id)] > 0) return prices[String(adult.id)];
  const vals = Object.values(prices ?? {}).filter((v) => v > 0);
  return vals.length ? Math.min(...vals) : null;
}

export function TourV2OfficialCard(props: {
  activityId: string;
  categories: OfficialCategory[];
  lang: string;
  onBook?: () => void;
  hideCta?: boolean;
  /** Clave i18n del encabezado (por defecto "Precios desde"). */
  headingKey?: string;
}) {
  const { t } = useTranslation();
  const money = useMoney();
  const { currency: shown } = useCurrency();
  const { data, isLoading } = useOfficialPrice(props.activityId, props.categories);
  const isGroup = (c: OfficialCategory) => (c.ticketCategory ?? "").toUpperCase() === "GROUP";
  const catLabel = (c: OfficialCategory) => {
    if (isGroup(c)) return t("tourV2.privateGroup", { defaultValue: "Grupo privado" });
    const key = (c.ticketCategory ?? "").toUpperCase().trim();
    const tr = key ? (t(`tourV2.cat.${key}`, { defaultValue: "" }) as string) : "";
    return tr || c.title;
  };
  const order: Record<string, number> = { ADULT: 0, YOUTH: 1, SENIOR: 1, STUDENT: 1, CHILD: 2, INFANT: 3 };
  // Solo categorías reales con precio en el canal oficial de producción.
  const rows = props.categories
    .filter((c) => data?.categoryPrices?.[String(c.id)] != null)
    .map((c) => ({ c, price: data?.categoryPrices[String(c.id)] ?? 0 }))
    .sort(
      (a, b) =>
        (order[(a.c.ticketCategory ?? "").toUpperCase()] ?? 9) -
        (order[(b.c.ticketCategory ?? "").toUpperCase()] ?? 9),
    );
  const age = (c: OfficialCategory) =>
    isGroup(c) || (c.minAge == null && c.maxAge == null) ? null : `${c.minAge ?? 0}–${c.maxAge ?? 99}`;

  return (
    <section
      aria-labelledby="v2-prices-heading"
      className="rounded-2xl border border-border bg-card p-5 shadow-[var(--shadow-elevated)] sm:p-6"
      data-testid="v2-prices-card"
    >
      <div className="mb-3 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Tag className="h-5 w-5 text-primary" aria-hidden />
          <h2 id="v2-prices-heading" className="text-lg font-semibold">
            {props.lang === "en" ? "Dates and travelers" : "Fechas y viajeros"}
          </h2>
        </div>
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-success" aria-hidden />
          {t("tourV2.live", { defaultValue: "En vivo" })}
        </span>
      </div>

      {isLoading ? (
        <ul className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <li key={i} className="h-10 animate-pulse rounded-md bg-muted/60" />
          ))}
        </ul>
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">
          {t("tourV2.officialNoPrices", { defaultValue: "Consulta los precios en el calendario de reserva." })}
        </p>
      ) : (
        <ul className="divide-y divide-border">
          {rows.map(({ c, price }) => (
            <li key={c.id} className="flex items-center justify-between gap-3 py-2.5 text-sm sm:text-base">
              <div className="min-w-0">
                <span className="font-medium text-foreground">{catLabel(c)}</span>
                {age(c) && <span className="ml-1 text-muted-foreground">({age(c)})</span>}
                {isGroup(c) && (
                  <span className="block text-xs text-muted-foreground">
                    {c.groupSize
                      ? t("tourV2.upToPeople", { count: c.groupSize, defaultValue: "Hasta {{count}} personas" })
                      : t("tourV2.perGroup", { defaultValue: "por grupo" })}
                  </span>
                )}
              </div>
              <div className="shrink-0 whitespace-nowrap font-semibold text-foreground">
                {price <= 0 ? t("tourV2.free", { defaultValue: "Gratis" }) : money(price, data?.currency ?? shown, 2)}
                {price > 0 && <span className="block text-right text-[10px] font-medium text-muted-foreground">{shown} · {isGroup(c) ? t("tourV2.perGroup", { defaultValue: "por grupo" }) : t("common.perPerson", { defaultValue: "por persona" })}</span>}
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="mt-3 text-xs text-muted-foreground">
        {t("tourV2.pricesIn", { currency: shown, defaultValue: "Precios en {{currency}}" })}
      </p>
    </section>
  );
}
